import React, { useState } from 'react';
import { Activity, FileText, Calendar, Clock, TrendingUp, AlertCircle, RefreshCw, User, Download } from 'lucide-react';
import PressureMapping from './gait/PressureMapping';
import GaitAsymmetry from './gait/GaitAsymmetry';
import LongitudinalAnalysis from './gait/LongitudinalAnalysis';
import GaitMetrics from './gait/GaitMetrics';
import GaitTabs from './gait/GaitTabs';

// Types for gait analysis data
export interface FootPressurePoint {
  x: number;
  y: number;
  pressure: number; // 0-100 scale
}

export interface StepData {
  id: string;
  timestamp: number;
  footSide: 'left' | 'right';
  pressurePoints: FootPressurePoint[];
  centerOfPressure: { x: number; y: number };
  peakPressure: number;
  contactArea: number;
  duration: number; // milliseconds
  stepLength: number; // centimeters
  propulsionForce: number; // newtons
}

export interface StrideData {
  id: string;
  timestamp: number;
  leftStep: StepData;
  rightStep: StepData;
  strideLength: number; // centimeters
  strideTime: number; // milliseconds
  symmetryIndex: number; // 0-100 scale, 100 being perfect symmetry
  cadence: number; // steps per minute
  velocity: number; // ft/second
  baseOfSupport: number; // centimeters
}

export interface GaitSession {
  id: string;
  date: Date;
  duration: number; // seconds
  strides: StrideData[];
  averageVelocity: number;
  averageCadence: number;
  averageStrideLength: number;
  averageSymmetryIndex: number;
  riskAssessment: {
    score: number; // 0-100, higher is more risk
    factors: {
      name: string;
      contribution: number; // 0-100
      change: number; // percent change from baseline
    }[];
  };
}

const AdvancedGaitAnalysis: React.FC = () => {
  // State for selected data and visualization options
  const [activeTab, setActiveTab] = useState<'pressure' | 'asymmetry' | 'longitudinal' | 'metrics'>('pressure');
  const [selectedSession, setSelectedSession] = useState<string>(mockGaitSessions[0].id);
  const [compareWithBaseline, setCompareWithBaseline] = useState<boolean>(true);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [selectedStride, setSelectedStride] = useState<string | null>(mockGaitSessions[0].strides[0].id);

  // Get current session and stride data
  const currentSession = mockGaitSessions.find(session => session.id === selectedSession) || mockGaitSessions[0];
  const currentStride = selectedStride 
    ? currentSession.strides.find(stride => stride.id === selectedStride) 
    : currentSession.strides[0];

  // Handlers
  const handleTabChange = (tab: 'pressure' | 'asymmetry' | 'longitudinal' | 'metrics') => {
    setActiveTab(tab);
  };

  const handleSessionChange = (sessionId: string) => {
    setSelectedSession(sessionId);
    // Reset stride selection when session changes
    setSelectedStride(mockGaitSessions.find(s => s.id === sessionId)?.strides[0].id || null);
    setIsPlaying(false);
  };

  const handleStrideChange = (strideId: string) => {
    setSelectedStride(strideId);
    setIsPlaying(false);
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleSpeedChange = (speed: number) => {
    setPlaybackSpeed(speed);
  };

  const handleCompareToggle = () => {
    setCompareWithBaseline(!compareWithBaseline);
  };

  // Render the appropriate component based on active tab
  const renderActiveTab = () => {
    switch (activeTab) {
      case 'pressure':
        return (
          <PressureMapping 
            session={currentSession}
            selectedStride={currentStride}
            isPlaying={isPlaying}
            playbackSpeed={playbackSpeed}
            onStrideChange={handleStrideChange}
          />
        );
      case 'asymmetry':
        return (
          <GaitAsymmetry 
            session={currentSession}
            compareWithBaseline={compareWithBaseline}
            selectedStride={currentStride}
            onStrideChange={handleStrideChange}
          />
        );
      case 'longitudinal':
        return (
          <LongitudinalAnalysis 
            sessions={mockGaitSessions}
            highlightedSession={currentSession}
          />
        );
      case 'metrics':
        return (
          <GaitMetrics 
            session={currentSession}
            selectedStride={currentStride}
            compareWithBaseline={compareWithBaseline}
          />
        );
      default:
        return (
          <PressureMapping 
            session={currentSession}
            selectedStride={currentStride}
            isPlaying={isPlaying}
            playbackSpeed={playbackSpeed}
            onStrideChange={handleStrideChange}
          />
        );
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Advanced Gait Analysis</h2>
          <p className="text-gray-600">Detailed stride-by-stride analysis with pressure mapping</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => {}}
            className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-md text-sm hover:bg-gray-50"
          >
            <FileText size={16} className="mr-2" />
            Export Report
          </button>
          <button 
            onClick={() => {}}
            className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
          >
            <Download size={16} className="mr-2" />
            Save Analysis
          </button>
        </div>
      </div>

      {/* Session Selector */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b">
          <h3 className="font-medium">Select Gait Session</h3>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {mockGaitSessions.map(session => (
            <button
              key={session.id}
              onClick={() => handleSessionChange(session.id)}
              className={`text-left p-3 rounded-lg border ${
                selectedSession === session.id 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex justify-between items-center">
                <span className="font-medium">
                  {session.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                {session.id === 'session-1' && (
                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">Latest</span>
                )}
                {session.id === 'session-4' && (
                  <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">Baseline</span>
                )}
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-sm text-gray-600">
                <div>
                  <div className="flex items-center">
                    <Activity size={14} className="mr-1" />
                    <span>
                      {session.strides.length} strides
                    </span>
                  </div>
                  <div className="flex items-center mt-1">
                    <Clock size={14} className="mr-1" />
                    <span>
                      {Math.round(session.duration / 60)} min
                    </span>
                  </div>
                </div>
                <div>
                  <div className="flex items-center">
                    <TrendingUp size={14} className="mr-1" />
                    <span>
                      {session.averageVelocity.toFixed(1)} ft/s
                    </span>
                  </div>
                  <div className="flex items-center mt-1">
                    <RefreshCw size={14} className="mr-1" />
                    <span>
                      Symmetry: {session.averageSymmetryIndex.toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>
              {session.riskAssessment.score > 70 && (
                <div className="mt-2 text-red-600 flex items-center text-sm">
                  <AlertCircle size={14} className="mr-1" />
                  <span>High fall risk detected</span>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Navigation Tabs */}
      <GaitTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        playbackControls={{
          isPlaying,
          playbackSpeed,
          onPlayPause: handlePlayPause,
          onSpeedChange: handleSpeedChange
        }}
        compareWithBaseline={compareWithBaseline}
        onCompareToggle={handleCompareToggle}
      />

      {/* Main Content Area */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {renderActiveTab()}
      </div>
    </div>
  );
};

// Mock data for the component
const mockGaitSessions: GaitSession[] = [
  {
    id: 'session-1',
    date: new Date(Date.now() - 86400000), // Yesterday
    duration: 720, // 12 minutes
    strides: Array.from({ length: 15 }, (_, i) => generateMockStride(`stride-${i+1}`, Date.now() - 86400000 + i*1000, i < 5 ? 0.9 : 0.85)),
    averageVelocity: 2.8,
    averageCadence: 98,
    averageStrideLength: 104,
    averageSymmetryIndex: 82,
    riskAssessment: {
      score: 65,
      factors: [
        { name: 'Gait Asymmetry', contribution: 35, change: 5 },
        { name: 'Step Variability', contribution: 20, change: 8 },
        { name: 'Reduced Velocity', contribution: 10, change: 3 }
      ]
    }
  },
  {
    id: 'session-2',
    date: new Date(Date.now() - 7 * 86400000), // 1 week ago
    duration: 780, // 13 minutes
    strides: Array.from({ length: 18 }, (_, i) => generateMockStride(`stride-${i+1}`, Date.now() - 7 * 86400000 + i*1000, 0.87)),
    averageVelocity: 2.9,
    averageCadence: 100,
    averageStrideLength: 106,
    averageSymmetryIndex: 84,
    riskAssessment: {
      score: 60,
      factors: [
        { name: 'Gait Asymmetry', contribution: 32, change: 2 },
        { name: 'Step Variability', contribution: 18, change: 5 },
        { name: 'Reduced Velocity', contribution: 10, change: 0 }
      ]
    }
  },
  {
    id: 'session-3',
    date: new Date(Date.now() - 14 * 86400000), // 2 weeks ago
    duration: 840, // 14 minutes
    strides: Array.from({ length: 20 }, (_, i) => generateMockStride(`stride-${i+1}`, Date.now() - 14 * 86400000 + i*1000, 0.88)),
    averageVelocity: 3.0,
    averageCadence: 102,
    averageStrideLength: 108,
    averageSymmetryIndex: 86,
    riskAssessment: {
      score: 55,
      factors: [
        { name: 'Gait Asymmetry', contribution: 30, change: 0 },
        { name: 'Step Variability', contribution: 15, change: 3 },
        { name: 'Reduced Velocity', contribution: 10, change: -2 }
      ]
    }
  },
  {
    id: 'session-4',
    date: new Date(Date.now() - 30 * 86400000), // 1 month ago - baseline
    duration: 900, // 15 minutes
    strides: Array.from({ length: 22 }, (_, i) => generateMockStride(`stride-${i+1}`, Date.now() - 30 * 86400000 + i*1000, 0.95)),
    averageVelocity: 3.1,
    averageCadence: 105,
    averageStrideLength: 112,
    averageSymmetryIndex: 90,
    riskAssessment: {
      score: 45,
      factors: [
        { name: 'Gait Asymmetry', contribution: 25, change: 0 },
        { name: 'Step Variability', contribution: 12, change: 0 },
        { name: 'Reduced Velocity', contribution: 8, change: 0 }
      ]
    }
  }
];

// Helper function to generate mock stride data
function generateMockStride(id: string, timestamp: number, symmetryFactor: number = 0.9): StrideData {
  // The symmetry factor (0-1) controls how symmetric the stride is
  // Lower values mean more asymmetry between left and right
  
  const leftStepLength = 50 + Math.random() * 10;
  const rightStepLength = leftStepLength * (symmetryFactor + (Math.random() * 0.1));
  
  const leftPropulsion = 400 + Math.random() * 50;
  const rightPropulsion = leftPropulsion * (symmetryFactor + (Math.random() * 0.1));
  
  const leftDuration = 550 + Math.random() * 50;
  const rightDuration = leftDuration * (symmetryFactor + (Math.random() * 0.1));

  const leftPeakPressure = 70 + Math.random() * 20;
  const rightPeakPressure = leftPeakPressure * (symmetryFactor + (Math.random() * 0.1));

  // Generate mock pressure points - simplified for this example
  function generatePressurePoints(side: 'left' | 'right', peakPressure: number): FootPressurePoint[] {
    const points: FootPressurePoint[] = [];
    const baseX = side === 'left' ? 20 : 60;
    const maxPoints = 50 + Math.floor(Math.random() * 20);
    
    // Heel points (higher pressure)
    for (let i = 0; i < maxPoints * 0.3; i++) {
      points.push({
        x: baseX + (Math.random() * 10) - 5,
        y: 80 + (Math.random() * 10),
        pressure: (peakPressure * 0.8) + (Math.random() * peakPressure * 0.2)
      });
    }
    
    // Mid-foot points (lower pressure)
    for (let i = 0; i < maxPoints * 0.3; i++) {
      points.push({
        x: baseX + (Math.random() * 10) - 5,
        y: 50 + (Math.random() * 20),
        pressure: (peakPressure * 0.5) + (Math.random() * peakPressure * 0.3)
      });
    }
    
    // Toe points (medium-high pressure)
    for (let i = 0; i < maxPoints * 0.4; i++) {
      points.push({
        x: baseX + (Math.random() * 12) - 6,
        y: 20 + (Math.random() * 15),
        pressure: (peakPressure * 0.7) + (Math.random() * peakPressure * 0.3)
      });
    }
    
    return points;
  }

  const leftStep: StepData = {
    id: `${id}-left`,
    timestamp: timestamp,
    footSide: 'left',
    pressurePoints: generatePressurePoints('left', leftPeakPressure),
    centerOfPressure: { x: 25, y: 50 },
    peakPressure: leftPeakPressure,
    contactArea: 120 + Math.random() * 30,
    duration: leftDuration,
    stepLength: leftStepLength,
    propulsionForce: leftPropulsion
  };

  const rightStep: StepData = {
    id: `${id}-right`,
    timestamp: timestamp + leftDuration,
    footSide: 'right',
    pressurePoints: generatePressurePoints('right', rightPeakPressure),
    centerOfPressure: { x: 65, y: 50 },
    peakPressure: rightPeakPressure,
    contactArea: 110 + Math.random() * 30,
    duration: rightDuration,
    stepLength: rightStepLength,
    propulsionForce: rightPropulsion
  };

  // Calculate symmetry index (0-100 scale, 100 is perfect symmetry)
  const stepLengthSymmetry = 100 - Math.abs((leftStepLength - rightStepLength) / ((leftStepLength + rightStepLength) / 2) * 100);
  const durationSymmetry = 100 - Math.abs((leftDuration - rightDuration) / ((leftDuration + rightDuration) / 2) * 100);
  const forceSymmetry = 100 - Math.abs((leftPropulsion - rightPropulsion) / ((leftPropulsion + rightPropulsion) / 2) * 100);
  
  const symmetryIndex = (stepLengthSymmetry + durationSymmetry + forceSymmetry) / 3;

  return {
    id,
    timestamp,
    leftStep,
    rightStep,
    strideLength: leftStepLength + rightStepLength,
    strideTime: leftDuration + rightDuration,
    symmetryIndex,
    cadence: 60000 / (leftDuration + rightDuration),
    velocity: (leftStepLength + rightStepLength) / ((leftDuration + rightDuration) / 1000) * 0.0328,
    baseOfSupport: 10 + Math.random() * 5
  };
}

export default AdvancedGaitAnalysis; 